import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Mail, Building2, Globe2, MapPin, ShieldCheck, Cpu, Crosshair, Sparkles,
  Coins, Users, BadgeCheck, Heart, Lightbulb, Eye, Handshake,
  Zap, Clock,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import DecorBackground from '@/components/marketing/DecorBackground';
import FAQAccordion from '@/components/marketing/FAQAccordion';
import { Button } from '@/components/ui/button';
import { breadcrumbJsonLd, organizationJsonLd, websiteJsonLd, graph } from '@/lib/jsonld';

/* -------------------------------------------------------------
   FAQ — also wired into FAQPage JSON-LD so Google AI can read it.
   ------------------------------------------------------------- */
const ABOUT_FAQ = [
  {
    q: 'What is Ads Dashbound?',
    a: 'Ads Dashbound is a performance traffic platform operated on Dashbound.media. It connects advertisers, agencies, and media buyers with curated traffic flows across formats such as zero-click, domain redirect, pop, push, in-page push, and direct click — paired with source-level controls, AI-assisted routing signals, fraud filtering, and a top-up based campaign launch model.',
  },
  {
    q: 'Who is Ads Dashbound for?',
    a: 'Ads Dashbound is built for serious advertisers, performance agencies, and media buyers running campaigns across iGaming, Finance and Trading, E-commerce, Sweepstakes, Software & Utilities, VPN, and Lead Generation verticals. It is not a self-serve tool for casual users — every advertiser is reviewed manually before launch.',
  },
  {
    q: 'Does Ads Dashbound have a login dashboard?',
    a: 'Yes. Approved advertisers receive access to a separate traffic panel for campaign management. The login link is available in the website header and footer. The marketing site at Dashbound.media is the public entry point — the panel itself is a separate product.',
  },
  {
    q: 'How do advertisers apply?',
    a: 'Advertisers submit an application through the /apply form, sharing their offers, target GEOs, traffic format preferences, and budget context. The Ads Dashbound team reviews each application manually and replies with format and GEO recommendations before any spend.',
  },
  {
    q: 'Does Ads Dashbound use fixed pricing plans?',
    a: 'No. Ads Dashbound runs a top-up funded model — there are no public pricing tables or monthly subscription tiers. Top-up minimums and recommended ranges depend on traffic format, GEO, and vertical, and are shared during onboarding.',
  },
  {
    q: 'Where is Ads Dashbound based?',
    a: 'Ads Dashbound is operated by DASHBOUND MEDIA - FZCO, registered in Dubai, United Arab Emirates. The team supports advertisers worldwide — exact contact and office details are listed on the Contact page.',
  },
  {
    q: 'Which markets and GEOs does Ads Dashbound cover?',
    a: 'Coverage spans six broad regions — North America, Europe, LATAM, MENA, Sub-Saharan Africa, and APAC. Exact GEO availability depends on the traffic format and vertical and is confirmed by your account manager during onboarding.',
  },
];

const FORMAT_LIST = [
  { slug: 'zero-click', label: 'Zero-Click' },
  { slug: 'domain-redirect', label: 'Domain Redirect' },
  { slug: 'pop', label: 'Pop' },
  { slug: 'push', label: 'Push' },
  { slug: 'in-page-push', label: 'In-Page Push' },
  { slug: 'direct-click', label: 'Direct Click' },
];

const FEATURE_LIST = [
  { slug: 'source-level-control', label: 'Source-Level Control', icon: Crosshair, accent: '#22D3EE' },
  { slug: 'ai-traffic-intelligence', label: 'AI Traffic Intelligence', icon: Cpu, accent: '#8B5CF6' },
  { slug: 'fraud-filtering', label: 'Fraud Filtering', icon: ShieldCheck, accent: '#34D399' },
  { slug: 'smart-optimization', label: 'Smart Optimization', icon: Sparkles, accent: '#3B82F6' },
  { slug: 'top-up-model', label: 'Top-Up Model', icon: Coins, accent: '#FBBF24' },
];

/* -------------------------------------------------------------
   Operating principles — what shapes our decisions.
   ------------------------------------------------------------- */
const PRINCIPLES = [
  {
    icon: Eye,
    accent: '#22D3EE',
    title: 'Transparent over impressive',
    body: 'We share real source-level reports, honest format trade-offs, and where things have failed. No invented case studies, no “100% fraud-free” claims, no guaranteed ROI.',
  },
  {
    icon: Handshake,
    accent: '#8B5CF6',
    title: 'Curated, not self-serve',
    body: 'Every advertiser is reviewed manually. The goal is fewer, healthier campaigns — not a public dashboard race to the bottom on quality.',
  },
  {
    icon: Lightbulb,
    accent: '#FBBF24',
    title: 'Tooling supports humans',
    body: 'AI signals, source scoring, and fraud filters reduce noise so that account managers can spend time on judgement calls — not on dashboards.',
  },
  {
    icon: Heart,
    accent: '#F472B6',
    title: 'Long-term over flashy',
    body: 'We optimize for advertisers who want a steady source of clean performance traffic — not for a launch wave that burns out in 30 days.',
  },
];

/* -------------------------------------------------------------
   Markets we serve — region cards (no fabricated metrics).
   ------------------------------------------------------------- */
const REGIONS = [
  { label: 'North America', color: '#22D3EE', examples: 'US · CA' },
  { label: 'Europe', color: '#3B82F6', examples: 'UK · DE · FR · ES · IT · NL · PL · Nordics' },
  { label: 'LATAM', color: '#34D399', examples: 'BR · MX · AR · CO · CL' },
  { label: 'MENA', color: '#FBBF24', examples: 'AE · SA · EG · MA · TR' },
  { label: 'Sub-Saharan Africa', color: '#A78BFA', examples: 'ZA · NG · KE' },
  { label: 'APAC', color: '#F472B6', examples: 'IN · ID · PH · TH · VN · JP · KR · AU' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

export default function AboutPage() {
  const reduced = useReducedMotion();
  const stg = reduced ? {} : stagger;
  const up = reduced ? {} : fadeUp;
  const initial = reduced ? false : 'hidden';

  // Combined JSON-LD: BreadcrumbList + Organization + WebSite + AboutPage + FAQPage
  const jsonLd = graph(
    breadcrumbJsonLd([
      { label: 'Home', path: '/' },
      { label: 'About' },
    ]),
    organizationJsonLd(),
    websiteJsonLd(),
    {
      '@type': 'AboutPage',
      name: 'About Ads Dashbound',
      url: 'https://dashbound.media/about',
      description:
        'Ads Dashbound is a performance traffic platform operated on Dashbound.media by DASHBOUND MEDIA - FZCO. Curated formats, source-level controls, AI-assisted routing, fraud filtering, and a top-up based launch model.',
      mainEntity: { '@id': 'https://dashbound.media/#organization' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: ABOUT_FAQ.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  );

  return (
    <>
      <SEOHead path="/about" jsonLd={jsonLd} />
      <PageHeader
        breadcrumbs={[{ label: 'About' }]}
        eyebrow="About Ads Dashbound"
        title="The performance traffic platform behind Dashbound.media"
        subtitle="Ads Dashbound is a performance traffic platform operated on Dashbound.media. It helps advertisers, agencies, and media buyers explore curated traffic formats with source-level controls, AI-assisted routing signals, fraud filtering, and top-up based campaign launch support."
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Button
            asChild
            className="btn-primary-gradient text-white rounded-xl px-6 py-5 text-[14px] font-semibold border-0"
            data-testid="about-apply-cta"
          >
            <Link to="/apply" className="inline-flex items-center gap-2">
              Apply as Advertiser <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl px-5 py-5 text-[14px] font-semibold transition-colors backdrop-blur-md"
            data-testid="about-contact-cta"
          >
            <a href="mailto:contact@dashbound.media" className="inline-flex items-center gap-2">
              <Mail className="h-4 w-4" /> contact@dashbound.media
            </a>
          </Button>
        </div>
      </PageHeader>

      {/* Entity facts panel */}
      <section
        className="relative bg-[#050B17] text-white isolate overflow-hidden"
        data-testid="about-entity-section"
      >
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="grid lg:grid-cols-3 gap-5"
          >
            <motion.div variants={up}>
              <FactCard icon={Building2} accent="#A78BFA" label="Brand" value="Ads Dashbound" />
            </motion.div>
            <motion.div variants={up}>
              <FactCard icon={Globe2} accent="#22D3EE" label="Domain" value="Dashbound.media" />
            </motion.div>
            <motion.div variants={up}>
              <FactCard icon={BadgeCheck} accent="#34D399" label="Operating Company" value="DASHBOUND MEDIA - FZCO" subtle />
            </motion.div>
            <motion.div variants={up}>
              <FactCard icon={Mail} accent="#22D3EE" label="Contact" value="contact@dashbound.media" href="mailto:contact@dashbound.media" />
            </motion.div>
            <motion.div variants={up}>
              <FactCard icon={MapPin} accent="#34D399" label="Office" value="Dubai Silicon Oasis — United Arab Emirates" subtle />
            </motion.div>
            <motion.div variants={up}>
              <FactCard icon={Users} accent="#FBBF24" label="Built for" value="Advertisers · Agencies · Media buyers" subtle />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Our Story — concise, factual */}
      <section
        className="relative bg-[#06101F] text-white isolate overflow-hidden"
        data-testid="about-story-section"
      >
        <DecorBackground variant="section" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.div variants={up}>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
                Our story
              </span>
              <h2 className="font-display mt-5 text-[28px] sm:text-[34px] font-bold tracking-[-0.025em] leading-[1.15] text-white max-w-3xl">
                Built by operators who buy and sell performance traffic
              </h2>
            </motion.div>
            <motion.div variants={up} className="mt-6 space-y-5 text-[15.5px] leading-[1.75] text-slate-300/90 max-w-3xl">
              <p>
                <strong className="text-white font-semibold">Ads Dashbound</strong> is built by a team that has spent years buying and selling performance traffic across iGaming, finance, e-commerce, sweepstakes, software, VPN, and lead generation funnels.
              </p>
              <p>
                We are intentionally not a self-serve product — every advertiser application is reviewed by a real person, every launch goes through a 72-hour close-watch window, and every account has a named human contact behind it.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What we do + How we work + What we won't claim */}
      <section className="relative bg-[#06101F] text-white isolate overflow-hidden">
        <DecorBackground variant="section" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="grid lg:grid-cols-3 gap-6"
          >
            <motion.div variants={up}>
              <BlockCard
                title="What we do"
                body="Ads Dashbound curates supply across six performance traffic formats and layers source-level controls, AI-assisted routing signals, fraud filtering, and human campaign review on top. The site at Dashbound.media is the public entry point for advertisers who want managed launch support."
              />
            </motion.div>
            <motion.div variants={up}>
              <BlockCard
                title="How we work"
                body="There is no public pricing page. Every advertiser application is reviewed by a real human before access is granted. We share format and GEO recommendations before you commit budget, and stay close during the first 72 hours after launch."
              />
            </motion.div>
            <motion.div variants={up}>
              <BlockCard
                title="What we won’t claim"
                body="We will never claim 100% fraud-free traffic, guaranteed ROI, or fully automated AI optimization. We will tell you which formats fit your offer, where they have historically performed, and where they have not."
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Traffic formats list */}
      <section className="relative bg-[#050B17] text-white isolate overflow-hidden">
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
            Traffic formats
          </span>
          <h2 className="font-display mt-4 text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white max-w-3xl">
            Six curated performance traffic formats
          </h2>
          <p className="mt-3 text-[15px] text-slate-300/90 max-w-3xl leading-[1.65]">
            Ads Dashbound supports a focused set of advertiser-facing traffic formats. Each format has its own page with details, examples, and recommended verticals.
          </p>
          <ul className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="about-formats-grid">
            {FORMAT_LIST.map((f) => (
              <li key={f.slug}>
                <Link
                  to={`/ad-formats/${f.slug}`}
                  data-testid={`about-format-link-${f.slug}`}
                  className="
                    group block rounded-xl
                    bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                    border border-white/10
                    backdrop-blur-md
                    p-3.5 text-center
                    text-[13px] font-semibold text-white
                    hover:border-[#7DDCFF]/35
                    hover:text-[#7DDCFF]
                    transition-colors
                  "
                >
                  {f.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Features list */}
          <span className="mt-14 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A78BFA] backdrop-blur-md">
            Platform features
          </span>
          <h2 className="font-display mt-4 text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white max-w-3xl">
            Source controls, AI signals, fraud filtering, top-up launches
          </h2>
          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="about-features-grid">
            {FEATURE_LIST.map((f) => {
              const Icon = f.icon;
              return (
                <li key={f.slug}>
                  <Link
                    to={`/features/${f.slug}`}
                    data-testid={`about-feature-link-${f.slug}`}
                    className="
                      group flex items-center gap-3 rounded-xl
                      bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                      border border-white/10
                      backdrop-blur-md
                      p-3.5
                      text-[13px] font-semibold text-white
                      hover:border-[#7DDCFF]/35 transition-colors
                    "
                  >
                    <span
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border shrink-0"
                      style={{
                        background: `linear-gradient(180deg, ${f.accent}26, ${f.accent}10)`,
                        borderColor: `${f.accent}55`,
                        color: f.accent,
                      }}
                      aria-hidden="true"
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="group-hover:text-[#7DDCFF] transition-colors">{f.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Principles — what shapes our decisions */}
      <section
        className="relative bg-[#06101F] text-white isolate overflow-hidden"
        data-testid="about-principles-section"
      >
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#34D399] backdrop-blur-md">
              How we operate
            </span>
            <h2 className="font-display mt-4 text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white">
              Principles that shape every decision
            </h2>
            <p className="mt-3 text-[15px] text-slate-300/90 leading-[1.65]">
              These are the rules we hold ourselves to internally. If you ever see us drift from any of them, call us out.
            </p>
          </div>
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
            data-testid="about-principles-grid"
          >
            {PRINCIPLES.map((p) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  variants={up}
                  className="
                    rounded-2xl
                    bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                    border border-white/10
                    backdrop-blur-xl
                    p-6
                    h-full
                    transition-colors hover:border-[#7DDCFF]/30
                  "
                >
                  <span
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{
                      background: `linear-gradient(180deg, ${p.accent}26, ${p.accent}10)`,
                      borderColor: `${p.accent}55`,
                      color: p.accent,
                      boxShadow: `0 0 18px ${p.accent}33`,
                    }}
                    aria-hidden="true"
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-[15.5px] font-semibold tracking-[-0.01em] text-white">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[13.5px] text-slate-300/90 leading-[1.7]">
                    {p.body}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Markets we serve */}
      <section
        className="relative bg-[#050B17] text-white isolate overflow-hidden"
        data-testid="about-markets-section"
      >
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
              Markets we serve
            </span>
            <h2 className="font-display mt-4 text-[26px] sm:text-[32px] font-bold tracking-[-0.02em] text-white">
              Global advertiser support
            </h2>
            <p className="mt-3 text-[15px] text-slate-300/90 leading-[1.65]">
              We support advertisers across six broad regions. Specific GEO availability depends on traffic format and vertical — your account manager will confirm what fits during onboarding.
            </p>
          </div>
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="about-markets-grid"
          >
            {REGIONS.map((r) => (
              <motion.div
                key={r.label}
                variants={up}
                className="
                  rounded-2xl
                  bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                  border border-white/10
                  backdrop-blur-xl
                  p-5
                  transition-colors hover:border-[#7DDCFF]/30
                "
              >
                <div className="flex items-center gap-3">
                  <span className="relative inline-flex h-2 w-2 shrink-0">
                    <span
                      className="absolute inline-flex h-full w-full rounded-full opacity-70 pulse-glow"
                      style={{ background: r.color }}
                    />
                    <span
                      className="relative inline-flex h-2 w-2 rounded-full"
                      style={{ background: r.color }}
                    />
                  </span>
                  <h3 className="font-display text-[15.5px] font-semibold tracking-[-0.01em] text-white">
                    {r.label}
                  </h3>
                </div>
                <p className="mt-2.5 text-[12.5px] text-slate-400/95 leading-relaxed">
                  Sample GEOs: <span className="text-slate-200">{r.examples}</span>
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Coverage / hours strip */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <CoverageStat icon={Globe2} accent="#22D3EE" label="Time-zone coverage" value="London → Singapore on the same business day" />
            <CoverageStat icon={Clock} accent="#A78BFA" label="Typical reply time" value="Within 24 hours · Telegram is usually faster" />
            <CoverageStat icon={Zap} accent="#34D399" label="Approved advertiser launch" value="From application review to first campaign — days, not weeks" />
          </div>
        </div>
      </section>      {/* FAQ — entity Q&A wired into FAQPage JSON-LD */}
      <section
        className="relative bg-[#06101F] text-white isolate overflow-hidden"
        data-testid="about-faq-section"
      >
        <DecorBackground variant="section" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#22D3EE] backdrop-blur-md">
            FAQ
          </span>
          <h2 className="font-display mt-4 text-[28px] sm:text-[34px] font-bold tracking-[-0.02em] text-white">
            Frequently asked about Ads Dashbound
          </h2>
          <div className="mt-7 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur-xl px-5 sm:px-6 shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
            <FAQAccordion items={ABOUT_FAQ} idPrefix="about-faq" dark />
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Ready when you are"
        title="Apply as advertiser to get a real recommendation"
        subtitle="Share your offers, target GEOs, and budget context. The team reviews each application manually."
        primaryTo="/apply"
      />
    </>
  );
}

/* -------------------------------------------------------------
   Local components
   ------------------------------------------------------------- */

function FactCard({ icon: Icon, accent = '#22D3EE', label, value, subtle, href }) {
  const inner = (
    <>
      <span
        className="inline-flex h-10 w-10 items-center justify-center rounded-xl border shrink-0"
        style={{
          background: `linear-gradient(180deg, ${accent}26, ${accent}10)`,
          borderColor: `${accent}55`,
          color: accent,
          boxShadow: `0 0 18px ${accent}33`,
        }}
        aria-hidden="true"
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="mt-4">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </span>
        <p
          className={
            subtle
              ? 'mt-1 text-[14.5px] text-slate-200/95 font-medium'
              : 'mt-1 font-display text-[16px] font-semibold text-white'
          }
        >
          {value}
        </p>
      </div>
    </>
  );
  const cls =
    'block h-full rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur-xl p-5 transition-colors hover:border-[#7DDCFF]/30';
  if (href) {
    return (
      <a href={href} className={cls} data-testid={`about-fact-${label.toLowerCase().replace(/\s+/g, '-')}`}>
        {inner}
      </a>
    );
  }
  return (
    <div className={cls} data-testid={`about-fact-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {inner}
    </div>
  );
}

function BlockCard({ title, body }) {
  return (
    <div
      className="
        rounded-2xl
        bg-gradient-to-b from-white/[0.05] to-white/[0.02]
        border border-white/10
        backdrop-blur-xl
        p-6
        h-full
      "
    >
      <h3 className="font-display text-[18px] font-semibold tracking-[-0.02em] text-white">{title}</h3>
      <p className="mt-3 text-[14.5px] text-slate-300/90 leading-[1.7]">{body}</p>
    </div>
  );
}

function CoverageStat({ icon: Icon, accent = '#22D3EE', label, value }) {
  return (
    <div
      className="
        rounded-2xl
        bg-gradient-to-b from-white/[0.05] to-white/[0.02]
        border border-white/10
        backdrop-blur-xl
        p-5
        flex items-start gap-3
      "
    >
      <span
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border shrink-0"
        style={{
          background: `linear-gradient(180deg, ${accent}26, ${accent}10)`,
          borderColor: `${accent}55`,
          color: accent,
          boxShadow: `0 0 16px ${accent}28`,
        }}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </span>
        <p className="mt-0.5 text-[13.5px] text-slate-200/95 font-medium leading-snug">
          {value}
        </p>
      </div>
    </div>
  );
}
